import { Dialog } from 'radix-ui';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { Button, useBrandConfig } from '@dx-ui/global-ui-prism';

type ActionButton = {
  adaDescription?: string;
  label?: string;
} & React.ComponentPropsWithRef<'button'>;

type ShouldEnableActionButton = ActionButton & { isEnabled?: boolean };

export type ActionButtonProps = {
  /** Props passed to each button type. */
  buttonOptions?: {
    cancel?: ActionButton;
    confirm?: ActionButton;
    reset?: ShouldEnableActionButton;
  };
  /** Hander called when cancel button is blurred */
  onCancel?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  /** Handler called when done button is blurred. */
  onConfirm?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  /** Handler called when reset is activated */
  onReset?: () => void;
  /** stacks action buttons in viewport widths md and up */
  shouldStackButtons?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
};

export function ActionButtons({
  buttonOptions = {},
  onReset,
  onConfirm,
  onCancel,
  shouldStackButtons = false,
}: ActionButtonProps) {
  const { t } = useTranslation('osc-dialog');
  const brandConfig = useBrandConfig();
  const isSurfaceEnabled = brandConfig.surfaceConfig.isEnabled;
  const { cancel, confirm, reset } = buttonOptions;
  const resetBtnEnabled = reset?.isEnabled && reset?.label;

  return (
    <div
      className={cx('sticky bottom-0 flex flex-col gap-4 px-1 pb-4 pt-2', {
        'bg-surface': isSurfaceEnabled,
        'bg-bg': !isSurfaceEnabled,
        'md:justify-between': resetBtnEnabled,
        'md:justify-end': !resetBtnEnabled,
        'md:flex-row': !shouldStackButtons,
      })}
    >
      {resetBtnEnabled ? (
        <div className="md:self-center">
          <button
            className="btn btn-xl btn-primary-link w-full rounded-none"
            data-testid="shop-modal-reset-cta"
            onClick={() => onReset?.()}
            ref={reset?.ref}
            type="button"
          >
            {reset?.label}
            {reset?.adaDescription ? (
              <span className="sr-only" aria-live="assertive" role="alert">
                {reset?.adaDescription}
              </span>
            ) : null}
          </button>
        </div>
      ) : null}
      <div
        className={cx('flex flex-col gap-3', {
          'md:flex-row': !shouldStackButtons,
        })}
      >
        <Dialog.Close asChild>
          <Button
            fill="solid"
            size="lg"
            label={confirm?.label ?? t('done')}
            accessibleLabel={
              confirm?.adaDescription
                ? `${confirm?.label ?? t('done')} ${confirm.adaDescription}`
                : undefined
            }
            onClick={onConfirm}
            ref={confirm?.ref}
            disabled={confirm?.disabled}
            data-testid="shop-modal-done-cta"
          />
        </Dialog.Close>
        <Dialog.Close asChild>
          <Button
            fill="outline"
            size="lg"
            label={cancel?.label ?? t('cancel')}
            accessibleLabel={
              cancel?.adaDescription
                ? `${cancel?.label ?? t('cancel')} ${cancel.adaDescription}`
                : undefined
            }
            onClick={onCancel}
            ref={cancel?.ref}
            disabled={cancel?.disabled}
          />
        </Dialog.Close>
      </div>
    </div>
  );
}
